import React, { Fragment, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DataTable from "react-data-table-component";
import { Button, Card, CardBody, Col, Container, Input, Row } from "reactstrap";
import { Breadcrumbs, H2, P } from "../../../../AbstractElements";
import { loadAgentTimesheetData, toLocalDateInputValue } from "./timesheetData";
import i18n from "../../../../i18n";

const formatDuration = (minutes) => {
  const totalMinutes = Number(minutes || 0);

  if (!Number.isFinite(totalMinutes) || totalMinutes <= 0) {
    return "-";
  }

  const hours = Math.floor(totalMinutes / 60);
  const remainingMinutes = totalMinutes % 60;

  if (!hours) {
    return `${remainingMinutes} min`;
  }

  return remainingMinutes ? `${hours} h ${remainingMinutes} min` : `${hours} h`;
};

const formatPosition = (row) => {
  if (row.latestLatitude === null || row.latestLongitude === null) {
    return "-";
  }

  return `${row.latestLatitude.toFixed(5)}, ${row.latestLongitude.toFixed(5)}`;
};

const Timesheets = () => {
  const [selectedDate, setSelectedDate] = useState(toLocalDateInputValue());
  const [timesheet, setTimesheet] = useState({
    agents: [],
    events: [],
    totals: {
      agents: 0,
      events: 0,
      appOpens: 0,
      ticketsCreated: 0,
      ticketsPrinted: 0,
      searches: 0,
    },
  });
  const [loadingTimesheet, setLoadingTimesheet] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    let isMounted = true;

    const fetchTimesheet = async () => {
      setLoadingTimesheet(true);
      setErrorMsg("");

      try {
        const report = await loadAgentTimesheetData({
          date: selectedDate,
          eventLimit: 1000,
        });

        if (isMounted) {
          setTimesheet(report);
        }
      } catch (error) {
        console.error(error);
        if (isMounted) {
          setErrorMsg(i18n.t('Dashboard_Users_TimesheetLoadError'));
          setTimesheet({
            agents: [],
            events: [],
            totals: {
              agents: 0,
              events: 0,
              appOpens: 0,
              ticketsCreated: 0,
              ticketsPrinted: 0,
              searches: 0,
            },
          });
        }
      } finally {
        if (isMounted) {
          setLoadingTimesheet(false);
        }
      }
    };

    fetchTimesheet();

    return () => {
      isMounted = false;
    };
  }, [selectedDate]);

  const columns = [
    {
      name: i18n.t('Dashboard_Users_Employee'),
      selector: (row) => (
        row.agentId ? (
          <Link to={`${process.env.PUBLIC_URL}/dashboard/users/${row.agentId}`}>
            {row.agentId}
          </Link>
        ) : row.agentKey
      ),
      sortable: true,
    },
    {
      name: i18n.t('Dashboard_RecentOrders_Name'),
      selector: (row) => row.agentName || row.agentEmail || "-",
      sortable: true,
    },
    {
      name: "Premier evenement",
      selector: (row) => row.firstSeenLabel || "-",
      sortable: true,
    },
    {
      name: "Dernier evenement",
      selector: (row) => row.lastSeenLabel || "-",
      sortable: true,
    },
    {
      name: i18n.t('Dashboard_Users_Hours'),
      selector: (row) => formatDuration(row.estimatedMinutes),
      sortable: true,
    },
    {
      name: "Tickets",
      selector: (row) => row.ticketCreatedCount,
      sortable: true,
    },
    {
      name: "Impressions",
      selector: (row) => row.ticketPrintedCount,
      sortable: true,
    },
    {
      name: "Recherches",
      selector: (row) => row.searchCount,
      sortable: true,
    },
    {
      name: "Ouvertures",
      selector: (row) => row.appOpenCount,
      sortable: true,
    },
    {
      name: "Derniere position",
      selector: (row) => formatPosition(row),
      sortable: true,
    },
  ];

  const paginationComponentOptions = {
    rowsPerPageText: i18n.t('Dashboard_Users_RowsPerPage'),
    rangeSeparatorText: "de",
    selectAllRowsItem: true,
    selectAllRowsItemText: i18n.t('Dashboard_Users_All'),
  };

  return (
    <Fragment>
      <Breadcrumbs mainTitle={i18n.t('Dashboard_Users_Timesheet')} parent={i18n.t('Dashboard_Breadcrumb_Users')} title={i18n.t('Dashboard_Users_Timesheet')} />
      <Container fluid={true}>
        <Row>
          <Col sm="12">
            <Card>
              <CardBody>
                <Row>
                  <Col md="8">
                    <H2 attrH2={{ className: "title" }}>{i18n.t('Dashboard_Users_Timesheet')}</H2>
                    <P>
                      Sources lues: render-postgres. {timesheet.totals.agents} agents, {timesheet.totals.events} evenements, {timesheet.totals.ticketsCreated} tickets, {timesheet.totals.ticketsPrinted} impressions, {timesheet.totals.searches} recherches.
                    </P>
                    {errorMsg ? <P attrPara={{ className: "text-danger" }}>{errorMsg}</P> : ""}
                  </Col>
                  <Col md="4" className="text-md-end">
                    <Input
                      aria-label="Date feuille de temps"
                      className="mb-2"
                      type="date"
                      value={selectedDate}
                      onChange={(event) => setSelectedDate(event.target.value)}
                    />
                    <Button color="primary" tag={Link} to={`${process.env.PUBLIC_URL}/dashboard/users/activite-terrain/coding-work`}>
                      Coding Work
                    </Button>
                  </Col>
                </Row>
                <Row className="m-t-20">
                  <Col md="12">
                    {loadingTimesheet ? (
                      <P>{i18n.t('Dashboard_Users_LoadingTimesheets')}</P>
                    ) : (
                      <div className="table-responsive support-table">
                        <DataTable
                          columns={columns}
                          data={timesheet.agents}
                          striped={true}
                          center={true}
                          pagination
                          paginationPerPage={100}
                          paginationRowsPerPageOptions={[100, 200, 400, 500]}
                          paginationComponentOptions={paginationComponentOptions}
                          noDataComponent={i18n.t('Dashboard_Users_NoTimesheets')}
                        />
                      </div>
                    )}
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </Fragment>
  );
};

export default Timesheets;
